import React, { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  Layout,
  TextField,
  Button,
  Banner,
  Toast,
  Frame,
  Loading,
  EmptyState,
  Spinner,
  ButtonGroup,
  Badge,
  InlineStack,
} from "@shopify/polaris";
import { RefreshIcon, SearchIcon } from "@shopify/polaris-icons";
import axios from "../../Assets/Lib/axios";
import StatsOverview from "./components/StatsOverview";
import CourseCard from "./components/CourseCard";
import ActiveConversions from "./components/ActiveConversions";
import BulkActionsBar from "./components/BulkActionsBar";
import { usePolling } from "./hooks/usePolling";
import { calculateStats } from "./utils/hlsHelpers";

/**
 * HLS Video Converter Dashboard
 * Senior-level production component with proper state management,
 * error handling, and performance optimization
 */
const HlsConverter = () => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [courses, setCourses] = useState([]);
  const [expandedCourses, setExpandedCourses] = useState(new Set());
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [activeConversions, setActiveConversions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, needs-conversion, partial, complete
  const [isLoading, setIsLoading] = useState(true);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ active: false, message: "", error: false });

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  /**
   * Fetch all courses with video data
   */
  const fetchCourses = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get("/hls-converter");
      setCourses(response.data.data.data || []);
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError("Failed to load courses. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch course videos when expanded
   */
  const fetchCourseVideos = useCallback(async (courseId) => {
    try {
      const response = await axios.get(`/hls-converter/course/${courseId}`);
      const courseData = response.data.data;

      setCourses((prev) =>
        prev.map((course) =>
          course.id === courseId
            ? { ...course, videos: courseData.playlists, videosLoaded: true }
            : course
        )
      );
    } catch (err) {
      console.error(`Error fetching videos for course ${courseId}:`, err);
      showToast("Failed to load course videos", true);
    }
  }, []);

  /**
   * Fetch active conversions
   */
  const fetchActiveConversions = useCallback(async () => {
    try {
      const response = await axios.get("/hls-converter/active");
      setActiveConversions(response.data.data || []);
    } catch (err) {
      console.error("Error fetching active conversions:", err);
    }
  }, []);

  /**
   * Refresh all data
   */
  const refreshData = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchCourses(), fetchActiveConversions()]);
    
    // Refresh expanded courses
    const expandedIds = Array.from(expandedCourses);
    for (const courseId of expandedIds) {
      await fetchCourseVideos(courseId);
    }
  }, [fetchCourses, fetchActiveConversions, fetchCourseVideos, expandedCourses]);

  // ============================================================================
  // POLLING HOOK - Smart auto-refresh
  // ============================================================================
  usePolling(
    async () => {
      await fetchActiveConversions();
      
      // Refresh expanded courses to update progress
      const expandedIds = Array.from(expandedCourses);
      for (const courseId of expandedIds) {
        await fetchCourseVideos(courseId);
      }
    },
    10000, // Poll every 10 seconds
    activeConversions.length > 0 // Only poll if there are active conversions
  );

  // ============================================================================
  // COURSE EXPANSION
  // ============================================================================

  /**
   * Toggle course expansion and load videos if needed
   */
  const toggleCourse = useCallback(
    async (courseId) => {
      const newExpandedCourses = new Set(expandedCourses);

      if (expandedCourses.has(courseId)) {
        newExpandedCourses.delete(courseId);
      } else {
        newExpandedCourses.add(courseId);
        
        // Load videos if not already loaded
        const course = courses.find((c) => c.id === courseId);
        if (!course?.videosLoaded) {
          await fetchCourseVideos(courseId);
        }
      }

      setExpandedCourses(newExpandedCourses);
    },
    [expandedCourses, courses, fetchCourseVideos]
  );

  // ============================================================================
  // VIDEO SELECTION
  // ============================================================================

  /**
   * Toggle individual video selection
   */
  const toggleVideoSelection = useCallback((videoId) => {
    setSelectedVideos((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(videoId)) {
        newSelection.delete(videoId);
      } else {
        newSelection.add(videoId);
      }
      return newSelection;
    });
  }, []);

  /**
   * Select all MP4 videos in a course
   */
  const selectAllInCourse = useCallback((course) => {
    if (!course.videos) return;

    const mp4Videos = course.videos
      .filter((v) => v.status === "mp4")
      .map((v) => v.id);

    setSelectedVideos((prev) => {
      const newSelection = new Set(prev);
      mp4Videos.forEach((id) => newSelection.add(id));
      return newSelection;
    });
  }, []);

  /**
   * Deselect all videos in a course
   */
  const deselectAllInCourse = useCallback((course) => {
    if (!course.videos) return;

    const courseVideoIds = course.videos.map((v) => v.id);

    setSelectedVideos((prev) => {
      const newSelection = new Set(prev);
      courseVideoIds.forEach((id) => newSelection.delete(id));
      return newSelection;
    });
  }, []);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedVideos(new Set());
  }, []);

  // ============================================================================
  // CONVERSION ACTIONS
  // ============================================================================

  /**
   * Convert selected videos
   */
  const convertSelectedVideos = useCallback(async () => {
    if (selectedVideos.size === 0) return;

    setIsConverting(true);

    try {
      const response = await axios.post("/hls-converter/convert", {
        playlist_ids: Array.from(selectedVideos),
      });

      const { converted, skipped } = response.data.data;

      showToast(
        `Started conversion for ${converted} video${converted !== 1 ? "s" : ""}${
          skipped > 0 ? `. Skipped ${skipped} (already HLS or converting)` : ""
        }`,
        false
      );

      // Clear selection
      clearSelection();

      // Refresh data after a short delay
      setTimeout(() => {
        refreshData();
      }, 2000);
    } catch (err) {
      console.error("Error converting videos:", err);
      showToast("Failed to start conversion. Please try again.", true);
    } finally {
      setIsConverting(false);
    }
  }, [selectedVideos, clearSelection, refreshData]);

  // ============================================================================
  // SEARCH & FILTER
  // ============================================================================

  /**
   * Filter courses based on search query and status filter
   */
  const filteredCourses = courses.filter((course) => {
    // Search filter
    if (searchQuery) {
      const title = getFirstNonNullValue(course.title)?.toLowerCase() || "";
      if (!title.includes(searchQuery.toLowerCase())) {
        return false;
      }
    }

    // Status filter
    if (filterStatus === "needs-conversion") {
      return course.mp4_count > 0 && course.hls_count === 0;
    } else if (filterStatus === "partial") {
      return course.mp4_count > 0 && course.hls_count > 0;
    } else if (filterStatus === "complete") {
      return course.mp4_count === 0 && course.hls_count > 0;
    }

    return true; // "all"
  });

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const showToast = useCallback((message, error = false) => {
    setToast({ active: true, message, error });
  }, []);

  const dismissToast = useCallback(() => {
    setToast({ active: false, message: "", error: false });
  }, []);

 /**
 * Extract readable text from multilingual field
 * Handles both string and {en: "", ar: ""} formats
 */
function getFirstNonNullValue(obj) {
  // Handle null/undefined
  if (!obj) return "Untitled";
  
  // If it's already a plain string
  if (typeof obj === 'string') {
    // Check if it's JSON string
    try {
      const parsed = JSON.parse(obj);
      return parsed.en || parsed.ar || Object.values(parsed).find(v => v) || "Untitled";
    } catch {
      // Not JSON, just return the string
      return obj;
    }
  }
  
  // If it's an object with language keys
  if (typeof obj === 'object' && obj !== null) {
    return obj.en || obj.ar || Object.values(obj).find(v => v !== null && v !== "") || "Untitled";
  }
  
  return "Untitled";
}

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  useEffect(() => {
    fetchCourses();
    fetchActiveConversions();
  }, [fetchCourses, fetchActiveConversions]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const stats = calculateStats(courses);

  // Filter counts for badges
  const needsConversionCount = courses.filter(
    (c) => c.mp4_count > 0 && c.hls_count === 0
  ).length;
  const partialCount = courses.filter(
    (c) => c.mp4_count > 0 && c.hls_count > 0
  ).length;
  const completeCount = courses.filter(
    (c) => c.mp4_count === 0 && c.hls_count > 0
  ).length;

  // ============================================================================
  // RENDER
  // ============================================================================

  const toastMarkup = toast.active ? (
    <Toast
      content={toast.message}
      onDismiss={dismissToast}
      error={toast.error}
    />
  ) : null;

  if (isLoading && courses.length === 0) {
    return (
      <Page title="HLS Video Converter">
        <div style={{ textAlign: "center", padding: "100px 0" }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  return (
      <Page
        title="🎬 HLS Video Converter"
        fullWidth
        secondaryActions={[
          {
            content: "Refresh",
            icon: RefreshIcon,
            onAction: refreshData,
            loading: isLoading,
          },
        ]}
      >
        {isLoading && <Loading />}

        {/* MAIN CONTENT - Max width for better readability */}
<div style={{ maxWidth: "1600px", margin: "0 auto", padding: "0 20px" }}>          <Layout>
            {/* Error Banner */}
            {error && (
              <Layout.Section>
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  {error}
                </Banner>
              </Layout.Section>
            )}

            {/* Stats Overview */}
            <Layout.Section>
              <StatsOverview stats={stats} />
            </Layout.Section>

            {/* Active Conversions */}
            {activeConversions.length > 0 && (
              <Layout.Section>
                <ActiveConversions conversions={activeConversions} />
              </Layout.Section>
            )}

            {/* Search & Filters */}
            <Layout.Section>
              <Card>
                <div style={{ padding: "16px" }}>
                  <div style={{ marginBottom: "12px" }}>
                    <TextField
                      placeholder="Search courses..."
                      value={searchQuery}
                      onChange={setSearchQuery}
                      prefix={<SearchIcon />}
                      clearButton
                      onClearButtonClick={() => setSearchQuery("")}
                    />
                  </div>

                  {/* Filter Buttons */}
                  <InlineStack gap="2">
                    <ButtonGroup variant="segmented">
                      <Button
                        pressed={filterStatus === "all"}
                        onClick={() => setFilterStatus("all")}
                      >
                        All Courses ({courses.length})
                      </Button>
                      <Button
                        pressed={filterStatus === "needs-conversion"}
                        onClick={() => setFilterStatus("needs-conversion")}
                      >
                        Need Conversion ({needsConversionCount})
                      </Button>
                      <Button
                        pressed={filterStatus === "partial"}
                        onClick={() => setFilterStatus("partial")}
                      >
                        Partial HLS ({partialCount})
                      </Button>
                      <Button
                        pressed={filterStatus === "complete"}
                        onClick={() => setFilterStatus("complete")}
                      >
                        Complete ({completeCount})
                      </Button>
                    </ButtonGroup>
                  </InlineStack>
                </div>
              </Card>
            </Layout.Section>

            {/* Courses List */}
            <Layout.Section>
              {filteredCourses.length === 0 ? (
                <Card>
                  <EmptyState
                    heading="No courses found"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>
                      {searchQuery || filterStatus !== "all"
                        ? "Try adjusting your filters"
                        : "No courses available"}
                    </p>
                  </EmptyState>
                </Card>
              ) : (
                filteredCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    isExpanded={expandedCourses.has(course.id)}
                    selectedVideos={selectedVideos}
                    onToggle={() => toggleCourse(course.id)}
                    onToggleVideo={toggleVideoSelection}
                    onSelectAll={() => selectAllInCourse(course)}
                    onDeselectAll={() => deselectAllInCourse(course)}
                  />
                ))
              )}
            </Layout.Section>
          </Layout>
        </div>

        {/* Bulk Actions Bar */}
        {selectedVideos.size > 0 && (
          <BulkActionsBar
            selectedCount={selectedVideos.size}
            onConvert={convertSelectedVideos}
            onClear={clearSelection}
            isConverting={isConverting}
          />
        )}

        {toastMarkup}
      </Page>
  );
};

export default HlsConverter;